import { IconTrendingDown, IconTrendingUp } from "@tabler/icons-react"

import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { useAuth } from "@/hooks/useAuth"

export function SectionCards() {
  const { user } = useAuth()
  
  // Get organization-specific data based on user role
  const getOrganizationData = () => {
    if (!user) return { revenue: '$0.00', customers: '0', accounts: '0' }
    
    if (user.role.includes('super_admin')) {
      // Super admin sees aggregated data across all venues
      return {
        revenue: '$1,250.00',
        customers: '1,234',
        accounts: '45,678'
      }
    }
    
    if (user.role.includes('venue_manager')) {
      // Venue manager sees only their organization's data
      return {
        revenue: '$425.00', // Only their org revenue
        customers: '234', // Only their org customers  
        accounts: '2,156' // Only their org accounts
      }
    }
    
    // Viewer sees limited data
    return {
      revenue: 'N/A', // No financial access
      customers: '156',
      accounts: '1,089'
    }
  }
  
  const orgData = getOrganizationData()
  
  return (
    <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs @xl/main:grid-cols-2 @5xl/main:grid-cols-4">
      {/* Revenue Card - Role-specific content */}
      {user?.role.includes('viewer') ? (
        <Card className="@container/card">
          <CardHeader>
            <CardDescription>Your Activity</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
              24
            </CardTitle>
            <CardAction>
              <Badge variant="outline">
                <IconTrendingUp />
                +15%
              </Badge>
            </CardAction>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1.5 text-sm">
            <div className="line-clamp-1 flex gap-2 font-medium">
              Actions this week <IconTrendingUp className="size-4" />
            </div>
            <div className="text-muted-foreground">
              Views, interactions tracked
            </div>
          </CardFooter>
        </Card>
      ) : (
        <Card className="@container/card">
          <CardHeader>
            <CardDescription>
              {user?.role.includes('super_admin') ? 'Total Revenue' : 'Organization Revenue'}
            </CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
              {orgData.revenue}
            </CardTitle>
            <CardAction>
              <Badge variant="outline">
                <IconTrendingUp />
                {user?.role.includes('super_admin') ? '+12.5%' : '+8.2%'}
              </Badge>
            </CardAction>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1.5 text-sm">
            <div className="line-clamp-1 flex gap-2 font-medium">
              {user?.role.includes('super_admin') ? 'Trending up this month' : 'Organization performance'} <IconTrendingUp className="size-4" />
            </div>
            <div className="text-muted-foreground">
              {user?.role.includes('super_admin') ? 'All venues performance' : `${user?.organization?.name || 'Organization'} metrics`}
            </div>
          </CardFooter>
        </Card>
      )}
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>
            {user?.role.includes('super_admin') ? 'New Customers' : 'Organization Customers'}
          </CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {orgData.customers}
          </CardTitle>
          <CardAction>
            <Badge variant="outline">
              <IconTrendingDown />
              {user?.role.includes('super_admin') ? '-20%' : '-5%'}
            </Badge>
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          <div className="line-clamp-1 flex gap-2 font-medium">
            {user?.role.includes('super_admin') ? 'Down 20% this period' : 'Slight decrease'} <IconTrendingDown className="size-4" />
          </div>
          <div className="text-muted-foreground">
            {user?.role.includes('super_admin') ? 'Acquisition needs attention' : 'Local market trends'}
          </div>
        </CardFooter>
      </Card>
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>
            {user?.role.includes('super_admin') ? 'Active Accounts' : 'Organization Accounts'}
          </CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {orgData.accounts}
          </CardTitle>
          <CardAction>
            <Badge variant="outline">
              <IconTrendingUp />
              {user?.role.includes('super_admin') ? '+12.5%' : '+6.8%'}
            </Badge>
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          <div className="line-clamp-1 flex gap-2 font-medium">
            {user?.role.includes('super_admin') ? 'Strong user retention' : 'Good retention'} <IconTrendingUp className="size-4" />
          </div>
          <div className="text-muted-foreground">
            {user?.role.includes('super_admin') ? 'Engagement exceed targets' : 'Meeting local goals'}
          </div>
        </CardFooter>
      </Card>
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>Growth Rate</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            4.5%
          </CardTitle>
          <CardAction>
            <Badge variant="outline">
              <IconTrendingUp />
              +4.5%
            </Badge>
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          <div className="line-clamp-1 flex gap-2 font-medium">
            Steady performance increase <IconTrendingUp className="size-4" />
          </div>
          <div className="text-muted-foreground">Meets growth projections</div>
        </CardFooter>
      </Card>
    </div>
  )
}
